import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { useDispatch } from "react-redux";
import { Combobox, ETypeOfNotification, InputColumnTableProps, InputTable, InputTextCustom, Modal, setNotification } from "medere-ui-framework";
import useResizeListener from "../../../../../hooks/useResizeListener";
import { RoomType } from "../../../../../interface/interface";
import {
    deleteRoomType,
    getRoomTypeNomenclatorsForCombo,
    RoomTypeNomenclatorDTO,
    saveRoomType,
} from "../../../../../services/RoomTypeController";
import LocationInlineEditActions from "../../../components/LocationInlineEditActions";
import "./RoomTypesModal.scss";

interface RoomTypesModalProps {
    visible: boolean;
    setVisible: (visible: boolean) => void;
    roomTypes: RoomType[];
    /** Se llama después de crear, editar o eliminar, para recargar tipos y habitaciones. */
    onChanged: () => Promise<void> | void;
}

interface FormState {
    roomtype: number;
    description: string;
    nomenclator: RoomTypeNomenclatorDTO | null;
}

interface FormErrors {
    description?: string;
    nomenclator?: string;
}

const DESCRIPTION_MAX_LENGTH = 255;

const emptyForm: FormState = { roomtype: -1, description: "", nomenclator: null };

function toNomenclatorOption(roomType: RoomType): RoomTypeNomenclatorDTO | null {
    if (roomType.nomenclator == null) return null;
    return {
        nomenclator: roomType.nomenclator,
        code: "",
        description: roomType.nomenclatordescription ?? "",
        codeanddescription: roomType.nomenclatordescription ?? "",
    };
}

function roomsLabel(quantity: number) {
    return quantity === 1 ? "1 habitación" : `${quantity} habitaciones`;
}

export default function RoomTypesModal({ visible, setVisible, roomTypes, onChanged }: Readonly<RoomTypesModalProps>) {
    const dispatch = useDispatch();
    const isMobile = useResizeListener(768);

    const [form, setForm] = useState<FormState>(emptyForm);
    const [errors, setErrors] = useState<FormErrors>({});
    const [saving, setSaving] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<RoomType | null>(null);
    const [deleting, setDeleting] = useState(false);
    const isEditing = form.roomtype > 0;

    const resetForm = () => {
        setForm(emptyForm);
        setErrors({});
    };

    // Al cerrar el modal se descarta lo que haya quedado a medio cargar
    useEffect(() => {
        if (!visible) {
            resetForm();
            setPendingDelete(null);
        }
    }, [visible]);

    const validate = (): FormErrors => {
        const next: FormErrors = {};
        if (!form.description.trim()) next.description = "Completá la descripción";
        if (!form.nomenclator) next.nomenclator = "Elegí el nomenclador";
        return next;
    };

    const handleSave = async () => {
        const validation = validate();
        setErrors(validation);
        if (Object.keys(validation).length > 0 || saving) return false;

        setSaving(true);
        try {
            await saveRoomType({
                roomtype: form.roomtype,
                description: form.description.trim(),
                nomenclator: form.nomenclator?.nomenclator ?? null,
            });
            dispatch(setNotification({
                title: isEditing ? "Cambios guardados" : "Tipo de habitación creado",
                icon: "pi pi-check-circle",
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            resetForm();
            await onChanged();
            return true;
        } catch (error: any) {
            const status = error?.response?.status;
            const body = error?.response?.data;
            if (status === 409) {
                setErrors({ description: "Ya existe un tipo de habitación con esa descripción" });
            } else if (status === 400 && typeof body === "string" && body.toLowerCase().includes("nomenclador")) {
                setErrors({ nomenclator: body });
            } else {
                dispatch(setNotification({
                    title: "No se pudieron guardar los cambios",
                    desc: "Intentá de nuevo.",
                    icon: "pi pi-times",
                    typeOfNotification: ETypeOfNotification.ERROR,
                }));
            }
            return false;
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (item: RoomType) => {
        setPendingDelete(null);
        setErrors({});
        setForm({ roomtype: item.roomtype, description: item.description, nomenclator: toNomenclatorOption(item) });
    };

    const handleDelete = async () => {
        if (!pendingDelete || deleting) return;
        setDeleting(true);
        try {
            await deleteRoomType(pendingDelete.roomtype);
            dispatch(setNotification({
                title: "Tipo de habitación eliminado",
                icon: "pi pi-check-circle",
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            if (form.roomtype === pendingDelete.roomtype) resetForm();
            setPendingDelete(null);
            await onChanged();
        } catch (error: any) {
            const inUse = error?.response?.status === 409;
            dispatch(setNotification({
                title: inUse ? "No se puede eliminar" : "No se pudo eliminar el tipo de habitación",
                desc: inUse ? "Está asignado a una o más habitaciones. Quitáselo antes de eliminarlo." : "Intentá de nuevo.",
                icon: inUse ? "pi pi-exclamation-triangle" : "pi pi-times",
                typeOfNotification: inUse ? ETypeOfNotification.WARNING : ETypeOfNotification.ERROR,
            }));
            setPendingDelete(null);
        } finally {
            setDeleting(false);
        }
    };

    const descriptionInput = (
        <InputTextCustom
            className={(errors.description ? "" : "border-grey") + " w-full room-types-description-input"}
            value={form.description}
            onEnter={() => { void handleSave(); }}
            onChange={(e) => {
                const description = String(e.target.value ?? "").slice(0, DESCRIPTION_MAX_LENGTH);
                setForm((current) => ({ ...current, description }));
                setErrors((current) => ({ ...current, description: undefined }));
            }}
            error={!!errors.description}
            caption={errors.description}
        />
    );

    const nomenclatorInput = (
        <Combobox
            className={(errors.nomenclator ? "" : "border-grey") + " w-full"}
            getItems={getRoomTypeNomenclatorsForCombo}
            optionLabel="codeanddescription"
            placeholder="Buscá por código o descripción"
            value={form.nomenclator}
            autoselectOnOneItem={false}
            setValue={(value: RoomTypeNomenclatorDTO | null) => {
                setForm((current) => ({ ...current, nomenclator: value }));
                if (value) setErrors((current) => ({ ...current, nomenclator: undefined }));
            }}
            noItemsCaption="No hay nomencladores con la etiqueta de internación"
            error={!!errors.nomenclator}
            caption={errors.nomenclator}
        />
    );

    const renderRowActions = (item: RoomType) => {
        const inUse = item.roomsquantity > 0;
        return (
            <div className="room-types-row-actions">
                <Button
                    type="button"
                    icon="pi pi-pencil"
                    className="p-button-text md-icon-btn room-types-icon-btn"
                    tooltip="Editar"
                    tooltipOptions={{ position: "top", event: "both" }}
                    aria-label={`Editar ${item.description}`}
                    onClick={() => startEdit(item)}
                />
                <Button
                    type="button"
                    icon="pi pi-trash"
                    className="p-button-text md-icon-btn room-types-icon-btn"
                    disabled={inUse}
                    tooltip={inUse ? `No se puede eliminar: está asignado a ${roomsLabel(item.roomsquantity)}` : "Eliminar"}
                    tooltipOptions={{ position: "top", event: "both", showOnDisabled: true }}
                    aria-label={`Eliminar ${item.description}`}
                    onClick={() => setPendingDelete(item)}
                />
            </div>
        );
    };

    const columns: InputColumnTableProps<RoomType>[] = [
        {
            header: "Descripción",
            className: "room-types-col room-types-col--description",
            body: (item) => item.description,
            input: descriptionInput,
        },
        {
            header: "Nomenclador de estadía",
            className: "room-types-col room-types-col--nomenclator",
            body: (item) => item.nomenclatordescription || "—",
            input: nomenclatorInput,
        },
        {
            header: "Habitaciones",
            className: "room-types-col room-types-col--rooms",
            body: (item) => item.roomsquantity,
        },
        {
            header: "",
            className: "room-types-col room-types-col--actions",
            input: isEditing ? (
                <LocationInlineEditActions visible onConfirm={() => { void handleSave(); }} onCancel={resetForm} />
            ) : (
                <Button
                    type="button"
                    icon="pi pi-plus-circle text-3xl"
                    className="buttonSecondary2 p-button-rounded location-inputtable-add-inline-btn"
                    tooltip="Agregar"
                    tooltipOptions={{ position: "top", event: "both" }}
                    aria-label="Agregar tipo de habitación"
                    onClick={() => { void handleSave(); }}
                />
            ),
            body: (item) => renderRowActions(item),
        },
    ];

    const deleteConfirmation = pendingDelete && (
        <div className="room-types-delete-confirm" role="alertdialog" aria-labelledby="room-types-delete-title">
            <div>
                <p id="room-types-delete-title" className="room-types-delete-title">¿Eliminar tipo de habitación?</p>
                <p className="room-types-delete-text">Se va a eliminar «{pendingDelete.description}».</p>
            </div>
            <div className="room-types-delete-actions">
                <Button type="button" label="Cancelar" className="buttonMain2" autoFocus onClick={() => setPendingDelete(null)} disabled={deleting} />
                <Button type="button" label="Eliminar" className="buttonMain" onClick={() => { void handleDelete(); }} loading={deleting} />
            </div>
        </div>
    );

    return (
        <Modal
            className="room-types-modal location-config-view"
            visible={visible}
            setVisible={setVisible}
            width={isMobile ? "w-medium" : "w-xlarge"}
            header="Tipos de habitación"
        >
            <p className="room-types-help">
                El nomenclador de cada tipo se usa para facturar los días de estadía de las habitaciones que lo tienen asignado.
                Una habitación sin tipo no factura estadía.
            </p>

            {deleteConfirmation}

            {isMobile ? (
                <div className="room-types-mobile">
                    <div className="room-types-mobile-form">
                        <div>
                            <label className="block mb-2">Descripción <span className="md-req">*</span></label>
                            {descriptionInput}
                        </div>
                        <div>
                            <label className="block mb-2">Nomenclador de estadía <span className="md-req">*</span></label>
                            {nomenclatorInput}
                        </div>
                        <div className="room-types-mobile-form-actions">
                            {isEditing && <Button type="button" label="Cancelar" className="buttonMain2" onClick={resetForm} />}
                            <Button
                                type="button"
                                label={isEditing ? "Guardar cambios" : "Agregar"}
                                icon={isEditing ? undefined : "pi pi-plus-circle"}
                                className="buttonMain"
                                loading={saving}
                                onClick={() => { void handleSave(); }}
                            />
                        </div>
                    </div>

                    <div className="location-mobile-card-list">
                        {roomTypes.map((item) => (
                            <article key={`room-type-${item.roomtype}`} className="location-mobile-card">
                                <div className="location-mobile-card-header">
                                    <h4 className="location-mobile-card-title">{item.description}</h4>
                                    {renderRowActions(item)}
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Nomenclador</span>
                                    <span className="location-mobile-card-value">{item.nomenclatordescription || "—"}</span>
                                </div>
                                <div className="location-mobile-card-row">
                                    <span className="location-mobile-card-key">Habitaciones</span>
                                    <span className="location-mobile-card-value">{item.roomsquantity}</span>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            ) : (
                <InputTable
                    className="location-inputtable-fluid room-types-inputtable"
                    columnGap={0}
                    headerDivClassName="font-semibold bg-bluegray-50"
                    dividerClassName="my-1 border-divider"
                    buttonColumnWidth={43}
                    items={roomTypes}
                    columns={columns}
                    onAdd={() => {}}
                    divBtnClassName="location-inputtable-hide-native-control"
                    divider
                    dividerInFirstItem
                />
            )}

            {roomTypes.length === 0 && (
                <p className="room-types-empty">Todavía no hay tipos de habitación cargados.</p>
            )}
        </Modal>
    );
}
